import { useInfiniteQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Video,
  Loader2,
} from 'lucide-react';
import { useState } from 'react';
import { callsApi, formatCallDuration, callStatusLabel, type Call } from '@/api/calls';
import { useAuthStore } from '@/store/authStore';
import { useCallStore } from '@/store/callStore';
import { useMobile } from './ChatPage';
import Avatar from '@/components/ui/Avatar';

type Filter = 'all' | 'missed';

export default function CallHistoryPage() {
  const navigate = useNavigate();
  const { setShowSidebar } = useMobile();
  const me = useAuthStore((s) => s.user);
  const startCall = useCallStore((s) => s.startCall);
  const callPhase = useCallStore((s) => s.phase);
  const [filter, setFilter] = useState<Filter>('all');

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['calls', 'history', filter],
    queryFn: ({ pageParam }) =>
      callsApi.getHistory({ cursor: pageParam as string | undefined, filter, limit: 30 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });

  const rows: Call[] = data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Header */}
      <div className="h-14 border-b border-surface-800 px-3 flex items-center gap-3 flex-shrink-0 bg-surface-900">
        <button
          onClick={() => { setShowSidebar(true); navigate('/'); }}
          className="md:hidden btn-ghost p-1.5 text-surface-400 -ml-1"
          title="Назад"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-surface-100 text-sm leading-tight">Звонки</p>
          <p className="text-xs text-surface-500 leading-tight">История звонков 1:1</p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setFilter('all')}
            className={`px-2.5 py-1 rounded text-xs ${filter === 'all' ? 'bg-brand-600 text-white' : 'text-surface-400 hover:text-surface-200'}`}
          >
            Все
          </button>
          <button
            onClick={() => setFilter('missed')}
            className={`px-2.5 py-1 rounded text-xs ${filter === 'missed' ? 'bg-brand-600 text-white' : 'text-surface-400 hover:text-surface-200'}`}
          >
            Пропущенные
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-10 text-surface-500">
            <Loader2 size={18} className="animate-spin mr-2" /> Загрузка…
          </div>
        )}
        {!isLoading && rows.length === 0 && (
          <div className="text-center py-16 text-surface-500 text-sm">
            {filter === 'missed' ? 'Нет пропущенных звонков' : 'История звонков пуста'}
          </div>
        )}

        <ul className="divide-y divide-surface-800">
          {rows.map((call) => {
            const outgoing = me && call.callerId === me.id;
            const peer = outgoing ? call.callee : call.caller;
            const isMissed = call.status === 'MISSED' && !outgoing;
            const Icon = call.mode === 'VIDEO' ? Video
              : isMissed                        ? PhoneMissed
              : outgoing                        ? PhoneOutgoing
              : PhoneIncoming;
            const iconColor =
              isMissed          ? 'text-red-400' :
              call.status === 'ENDED' ? 'text-emerald-400' :
              'text-surface-400';
            return (
              <li key={call.id}>
                <div className="flex items-center gap-3 px-3 py-3 hover:bg-surface-800/50">
                  <Avatar
                    src={peer.avatarUrl}
                    username={peer.username}
                    size="md"
                    status={peer.status}
                  />
                  <button
                    onClick={() => {
                      setShowSidebar(false);
                      navigate(`/dm/${peer.id}`);
                    }}
                    className="flex-1 min-w-0 text-left"
                  >
                    <p className="text-surface-100 text-sm font-medium truncate">
                      {peer.displayName}
                    </p>
                    <p className="text-xs text-surface-500 flex items-center gap-1.5 mt-0.5">
                      <Icon size={12} className={iconColor} />
                      {me && callStatusLabel(call, me.id)}
                      <span>•</span>
                      <span>{new Date(call.startedAt).toLocaleString('ru-RU')}</span>
                      {call.durationSeconds != null && (
                        <>
                          <span>•</span>
                          <span className="tabular-nums">{formatCallDuration(call.durationSeconds)}</span>
                        </>
                      )}
                    </p>
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      disabled={callPhase !== 'idle'}
                      onClick={() => startCall(peer.id, 'VOICE', peer.displayName)}
                      className="btn-ghost p-2 text-surface-300 hover:text-brand-300 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Голосовой звонок"
                    >
                      <Phone size={16} />
                    </button>
                    <button
                      type="button"
                      disabled={callPhase !== 'idle'}
                      onClick={() => startCall(peer.id, 'VIDEO', peer.displayName)}
                      className="btn-ghost p-2 text-surface-300 hover:text-brand-300 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Видеозвонок"
                    >
                      <Video size={16} />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        {hasNextPage && (
          <div className="p-4 text-center">
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="btn-ghost text-sm text-brand-400"
            >
              {isFetchingNextPage ? 'Загрузка…' : 'Показать больше'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
